"use client";

import type { ReactNode } from "react";
import { useAuth } from "@/components/auth/AuthProvider";
import { hasMemberAccess } from "@/lib/authz/policy";
import { useIsPreviewingFreeUser } from "@/lib/home/freePreview";

/**
 * Renders its children for everyone except a paying subscriber — used to drop
 * the conversion copy (the per-tool upsell card and the shell's "these free
 * tools are a taste" banner) from `/free/**` pages, where a subscriber is just
 * using one of their tools and should not be sold the plan they already pay for.
 *
 * "Subscriber" is the same test AppHeader uses for its nav branch: admin or a
 * personal subscription id, unless an admin is previewing as a free user.
 *
 * The children render while auth is loading. These pages prerender as static
 * HTML with the signed-out view, so rendering the same thing on the first
 * client pass keeps hydration matching; the copy disappears once the session
 * resolves to a subscriber.
 */
export function HideForSubscribers({ children }: { children: ReactNode }) {
  const { isAdmin, billing, permissions, loading } = useAuth();
  const previewingFree = useIsPreviewingFreeUser(isAdmin);
  const isSubscriber = hasMemberAccess({ isAdmin, billing, permissions }) && !previewingFree;
  if (!loading && isSubscriber) return null;
  return <>{children}</>;
}
